import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '../ui';
import { Icone } from '../ui/icones';
import { CartaoDeEstado } from '../pages/auth/States';

type Props = { children: ReactNode; area: string };
type State = { hasError: boolean };

/**
 * Boundary por area (documento 07.4): um erro no MKT não derruba CRM nem Flow.
 *
 * O que ela desenha é o mesmo cartão das telas 403/404/500
 * (`CartaoDeEstado`), de propósito sem `Link`, sem `useAuth` e sem nada que
 * dependa de contexto: quando a árvore quebrou, o contexto pode ter
 * quebrado junto. As duas ações são locais: montar de novo o que caiu, e
 * recarregar a página inteira.
 *
 * Quando o Sentry entrar, é aqui que o eventId é capturado e mostrado.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[${this.props.area}]`, error, info.componentStack);
  }

  override render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <CartaoDeEstado
        code="500"
        rotuloDoCodigo="Algo não carregou"
        icone={<Icone.AlertaTriangulo tamanho={13} />}
        title={<>Esta parte <mark>não carregou</mark>.</>}
        description="Não foi nada que você fez. O resto do Suite continua funcionando; em geral basta tentar de novo."
        nota={
          <>
            Se continuar acontecendo, avise a equipe responsável pela conta e informe a área <code>{this.props.area}</code>.
          </>
        }
        acoes={
          <>
            <Button onClick={() => this.setState({ hasError: false })}>Tentar novamente</Button>
            <Button variant="secondary" onClick={() => window.location.reload()}>Recarregar a página</Button>
          </>
        }
      />
    );
  }
}
